import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  generateFullAlphabet,
  generateNumbersOneToHundred,
  generateMathProblems,
  generateNumberSenseCard,
  NUMBER_SENSE_COLORS,
  NUMBER_SENSE_SHAPES,
  NumberSenseMixMode,
  shouldMixNumberSenseCard,
  buildNumberSenseDotCounts,
  generatePeriodicTableElements,
  generateScienceTerms,
  generateNursingTerms,
  generateFullPeriodicTable,
  generateRandomNumbers,
  generateRandomUppercaseLetters,
  generateRandomLowercaseLetters,
  generateRandomMixedCaseLetters,
  generateNumbersOneToTwenty,
  generateDominoComparisons,
} from "../../utils/RandomGenerators";
import { saveCollection, getCurrentUser } from "../../api";
import { useAuth0 } from "@auth0/auth0-react";
import { User } from "../../types/user";
import { useTheme } from "../../context/ThemeContext";
import { VisibilityStates } from "../../types/VisibilityStates";
import { tourStepsCollectionSetup } from "./tourStepsCollectionSetup";
import { Step } from "react-joyride";
import GuidedTour from "../../components/GuidedTour";
import { v4 as uuidv4 } from "uuid";
import BubbleText from "../../components/BubbleText";
import { verifyImage } from "../../utils/imageVerification";
import {
  DEFAULT_PICTURE_QUIZ_PROMPT,
  PICTURE_QUIZ_CATEGORY,
} from "../../utils/pictureQuizUtils";
import { PICTURE_WORDS_CATEGORY } from "../../utils/pictureWordsUtils";
import { ColorblindImage } from "../../components/ColorblindImage";
import PairSlideImages from "../../components/PairSlideImages";
import { slideImageFields } from "../../utils/slideImages";

type Operation =
  | "multiplication"
  | "addition"
  | "subtraction"
  | "division"
  | "PeriodicElement";
interface ImageWithCount {
  id: string;
  file: File;
  preview: string;
  secondPreview?: string;
  count?: number;
  type?: string;
  question?: string;
  answer?: string;
  answerError?: boolean;
  word?: string;
  wordError?: boolean;
}

const preventNumberInputScroll = (
  event: React.WheelEvent<HTMLInputElement>,
) => {
  event.currentTarget.blur();
};

const toggleUniqueValue = (
  value: string,
  setValues: React.Dispatch<React.SetStateAction<string[]>>,
) => {
  setValues((current) => {
    if (current.includes(value)) {
      if (current.length === 1) {
        return current;
      }
      return current.filter((item) => item !== value);
    }
    return [...current, value];
  });
};

const CollectionSetup: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const { collectionName, isPublic, category, stage = "none" } = location.state || {};
  const { theme } = useTheme();
  const [file, setFile] = useState<File[]>([]);
  const [itemCount, setItemCount] = useState<number>(1);
  const [collectionItemCount, setCollectionItemCount] = useState<number>(1);
  const [sequence, setSequence] = useState<
    Array<{ name: string; svg?: string; count?: number }>
  >([]);
  const [type, setType] = useState<string>("letters");
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [operation, setOperation] = useState<Operation | null>(null);
  const [dotColors, setDotColors] = useState<string[]>(["blue"]);
  const [dotShapes, setDotShapes] = useState<string[]>(["circle"]);
  const [dotMixMode, setDotMixMode] = useState<NumberSenseMixMode>("never");
  const [dotCountType, setDotCountType] = useState<"fixed" | "random">("random");
  const [minDots, setMinDots] = useState<number>(1);
  const [maxDots, setMaxDots] = useState<number>(10);
  const [isGenerated, setIsGenerated] = useState<boolean>(false);
  const [previewSequence, setPreviewSequence] = useState<
    Array<{ name: string; svg?: string; count?: number }>
  >([]);
  const [visibilityStates, setVisibilityStates] = useState<VisibilityStates>({
    isDotCountTypeVisible: true,
    isMinDotsVisible: true,
    isMaxDotsVisible: true,
    isTypeSelectVisible: true,
    isItemCountVisible: true,
    isCollectionItemCountVisible: true,
    isDotColorVisible: true,
    isDotShapeVisible: true,
    isGenerateRandomSequenceButtonVisible: true,
    isSubmitButtonVisible: true,
    isFileUploadVisible: true,
    isClearButtonVisible: true,
    isNextButtonVisible: true,
    isGeneratedSequencePreviewVisible: false,
    isNameInputVisible: false,
    isAddNameButtonVisible: false,
    isSpinButtonVisible: false,
    isNamesListVisible: false,
    isCollectionNameVisible: false,
    isCategorySelectVisible: false,
    isStageSelectVisible: false,
    isPublicCheckboxVisible: false,
    isCollectionCardVisible: false,
    isStartCollectionButtonVisible: false,
    isEditCollectionButtonVisible: false,
    isDeleteCollectionButtonVisible: false,
    isSessionSettingsModalVisible: false,
    isEditCollectionModalVisible: false,
    isDuplicateCollectionModalVisible: false,
    isCollectionPreviewModalVisible: false,
  });
  const [isTourRunning, setIsTourRunning] = useState<boolean>(false);
  const [currentTourStep, setCurrentTourStep] = useState<number>(0);
  const [images, setImages] = useState<ImageWithCount[]>([]);
  const [pictureQuizSaveAttempted, setPictureQuizSaveAttempted] =
    useState(false);
  const [pictureWordsSaveAttempted, setPictureWordsSaveAttempted] =
    useState(false);
  const [newItemName, setNewItemName] = useState<string>("");
  const [minDominoDots, setMinDominoDots] = useState<number>(1);
  const [maxDominoDots, setMaxDominoDots] = useState<number>(6);
  const [dominoDotColor, setDominoDotColor] = useState<string>("black");

  // Define the steps variable
  const steps: Step[] = tourStepsCollectionSetup(
    visibilityStates,
    category,
    type,
    dotCountType,
  );
  // Example of updating visibility states based on some condition
  useEffect(() => {
    // Update visibility states based on your logic
    setVisibilityStates((prev) => ({
      ...prev,
      isGeneratedSequencePreviewVisible: true,
    }));
  }, []);
  // Call createTourSteps with updated visibilityStates
  useEffect(() => {
    console.log("Generated tour steps:", steps); // Debugging log for steps
    if (isTourRunning) {
      console.log("Tour is running with steps:", steps);
    }
  }, [visibilityStates, isTourRunning]); // Regenerate steps when visibility states change
  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const fetchedUser = await getCurrentUser(getAccessTokenSilently);
        if (isUser(fetchedUser)) {
          const userWithDefaults: User = {
            ...fetchedUser,
            id: fetchedUser.id || generateId(),
            name: fetchedUser.name || "Default Name",
            username: fetchedUser.username || "defaultUsername",
            email: fetchedUser.email || "default@example.com",
          };
          setCurrentUser(userWithDefaults);
        } else {
          console.error(
            "Fetched user is missing required properties:",
            fetchedUser,
          );
        }
      } catch (error) {
        console.error("Error fetching current user:", error);
      }
    };
    fetchUser();
  }, [getAccessTokenSilently]);
  // Set type based on category
  useEffect(() => {
    switch (category) {
      case "Math":
        setType("numbers");
        break;
      case "Language Arts":
        setType("letters");
        break;
      case "Number Sense":
        setType("numberSense");
        break;
      case "Science":
        setType("scienceTerms");
        break;
      case "Nursing":
        setType("nursingTerms");
        break;
      case PICTURE_QUIZ_CATEGORY:
        setType("pictureQuiz");
        break;
      case PICTURE_WORDS_CATEGORY:
        setType("pictureWords");
        break;
      default:
        setType("letters");
    }
    console.log("Current type set to:", type); // Debugging log for type
  }, [category]);
  // Set default operation for math problems
  useEffect(() => {
    if (type === "mathProblems" && !operation) {
      setOperation("addition"); // Set a default operation
    }
  }, [type, operation]);
  // Start the tour based on a specific condition
  useEffect(() => {
    // Check if the tour has been completed before
    const tourCompleted = localStorage.getItem("tourCompleted");
    // Start the tour if it hasn't been completed
    if (!tourCompleted) {
      setIsTourRunning(true);
    }
  }, []);
  // Update visibility states based on the selected type
  useEffect(() => {
    const newVisibilityStates: VisibilityStates = {
      ...visibilityStates, // Spread existing visibility states
      isDotCountTypeVisible: type === "numberSense",
      isMinDotsVisible: dotCountType === "random",
      isMaxDotsVisible: dotCountType === "random",
      isTypeSelectVisible: true,
      isItemCountVisible: true,
      isCollectionItemCountVisible: true,
      isDotColorVisible: true,
      isDotShapeVisible: true,
      isGenerateRandomSequenceButtonVisible: true,
      isFileUploadVisible: true,
      isClearButtonVisible: true,
      isSubmitButtonVisible: true,
      isNextButtonVisible: true,
      isGeneratedSequencePreviewVisible: isGenerated,
      isNameInputVisible: false,
      isAddNameButtonVisible: false,
      isSpinButtonVisible: false,
      isNamesListVisible: false,
      isCollectionNameVisible: false,
      isCategorySelectVisible: false,
      isStageSelectVisible: false,
      isPublicCheckboxVisible: false,
      isCollectionCardVisible: false,
      isStartCollectionButtonVisible: false,
      isEditCollectionButtonVisible: false,
      isDeleteCollectionButtonVisible: false,
      isSessionSettingsModalVisible: false,
      isEditCollectionModalVisible: false,
      isDuplicateCollectionModalVisible: false,
    };
    setVisibilityStates(newVisibilityStates);
  }, [type, dotCountType, isGenerated]); // Add dependencies as needed
  const handleTourComplete = () => {
    setIsTourRunning(false);
    localStorage.setItem("tourCompleted", "true"); // Mark the tour as completed
  };
  const handleTourStepChange = (step: number) => {
    setCurrentTourStep(step);
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      const validFiles: File[] = [];
      const errors: string[] = [];

      // Verify each file
      for (const file of selectedFiles) {
        const result = await verifyImage(file);
        if (result.isValid) {
          validFiles.push(file);
        } else {
          errors.push(`${file.name}: ${result.message}`);
        }
      }

      // Process valid files
      if (validFiles.length > 0) {
        setFile(prevFiles => [...prevFiles, ...validFiles]);
        
        validFiles.forEach(selectedFile => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const newItem: ImageWithCount = {
              id: uuidv4(),
              file: selectedFile,
              preview: reader.result as string,
              count: category === "Number Sense" ? 1 : undefined,
              question:
                category === PICTURE_QUIZ_CATEGORY
                  ? DEFAULT_PICTURE_QUIZ_PROMPT
                  : undefined,
              answer: category === PICTURE_QUIZ_CATEGORY ? "" : undefined,
              word: category === PICTURE_WORDS_CATEGORY ? "" : undefined,
            };
            setImages((prevImages) => [...prevImages, newItem]);
          };
          reader.readAsDataURL(selectedFile);
        });
      }

      // Show errors if any
      if (errors.length > 0) {
        alert(`Some files were not added:\n${errors.join("\n")}`);
      }

      // Reset the file input value after handling the files
      e.target.value = "";
    }
  };
  const handleSecondImageChange = (
    imageId: string,
    secondPreview: string | undefined,
  ) => {
    setImages((prevImages) =>
      prevImages.map((image) =>
        image.id === imageId ? { ...image, secondPreview } : image,
      ),
    );
  };
  const handleImageCountChange = (imageId: string, newCount: number) => {
    setImages((prevImages) =>
      prevImages.map((image) =>
        image.id === imageId ? { ...image, count: newCount } : image,
      ),
    );
  };
  const handlePictureQuizQuestionChange = (imageId: string, question: string) => {
    setImages((prevImages) =>
      prevImages.map((image) =>
        image.id === imageId ? { ...image, question } : image,
      ),
    );
  };
  const handlePictureQuizAnswerChange = (imageId: string, answer: string) => {
    setImages((prevImages) =>
      prevImages.map((image) =>
        image.id === imageId
          ? { ...image, answer, answerError: false }
          : image,
      ),
    );
  };
  const validatePictureQuizImages = (): boolean => {
    if (images.length === 0) {
      alert("Please upload at least one image.");
      return false;
    }

    const hasMissingAnswers = images.some(
      (image) => !image.answer?.trim(),
    );
    if (hasMissingAnswers) {
      setPictureQuizSaveAttempted(true);
      setImages((prevImages) =>
        prevImages.map((image) => ({
          ...image,
          answerError: !image.answer?.trim(),
        })),
      );
      alert("Please enter an answer for each image.");
      return false;
    }

    return true;
  };
  const handlePictureWordsWordChange = (imageId: string, word: string) => {
    setImages((prevImages) =>
      prevImages.map((image) =>
        image.id === imageId
          ? { ...image, word, wordError: false }
          : image,
      ),
    );
  };
  const validatePictureWordsImages = (): boolean => {
    if (images.length === 0) {
      alert("Please upload at least one image.");
      return false;
    }

    const hasMissingWords = images.some((image) => !image.word?.trim());
    if (hasMissingWords) {
      setPictureWordsSaveAttempted(true);
      setImages((prevImages) =>
        prevImages.map((image) => ({
          ...image,
          wordError: !image.word?.trim(),
        })),
      );
      alert("Please enter a word for each image.");
      return false;
    }

    return true;
  };
  const generateRandomSequence = () => {
    const uniqueMax = getMaxUniqueItemsForType(type);
    const hardLimit = getEffectiveItemLimit(type);
    let countToGenerate = Number.isFinite(itemCount) && itemCount > 0 ? itemCount : 1;

    if (countToGenerate > hardLimit) {
      const reason =
        uniqueMax !== null
          ? `This type only has ${uniqueMax} unique items.`
          : `Please keep collections to ${SOFT_MAX_GENERATED_ITEMS} items or fewer so the page stays responsive.`;
      alert(
        `You asked for ${countToGenerate} items. ${reason} Generating ${hardLimit} instead.`,
      );
      countToGenerate = hardLimit;
      setItemCount(hardLimit);
    }

    let generatedSequence: Array<{
      name: string;
      svg?: string;
      count?: number;
    }> = [];

    switch (type) {
      case "numbers":
        generatedSequence = generateRandomNumbers(countToGenerate).map(
          (num) => ({ name: num }),
        );
        break;
      case "letters":
        generatedSequence = generateRandomUppercaseLetters(countToGenerate).map(
          (letter) => ({ name: letter }),
        );
        break;
      case "randomLowercase":
        generatedSequence = generateRandomLowercaseLetters(countToGenerate).map(
          (letter) => ({ name: letter }),
        );
        break;
      case "randomMixedCase":
        generatedSequence = generateRandomMixedCaseLetters(countToGenerate).map(
          (letter) => ({ name: letter }),
        );
        break;
      case "alphabet":
        generatedSequence = generateFullAlphabet().map((letter) => ({
          name: letter,
        }));
        break;
      case "numbersOneToHundred":
        generatedSequence = generateNumbersOneToHundred().map((num) => ({
          name: num.toString(),
        }));
        break;
      case "mathProblems":
        if (operation && operation !== "PeriodicElement") {
          const mathProblems = generateMathProblems(countToGenerate, operation);
          generatedSequence = mathProblems.map((item) => ({
            name: item.problem,
            count: item.answer,
          }));
        } else {
          console.error(
            "Math Problems selected but operation is not set or is PeriodicElement",
          );
          alert("Please choose a math operation before generating.");
          return;
        }
        break;
      case "numberSense": {
        const cardsToGenerate = clampItemCount(
          collectionItemCount,
          "numberSense",
        );
        if (collectionItemCount > cardsToGenerate) {
          alert(
            `Please keep number sense cards to ${SOFT_MAX_GENERATED_ITEMS} or fewer. Generating ${cardsToGenerate} instead.`,
          );
          setCollectionItemCount(cardsToGenerate);
        }
        const generatedItems = [];
        const dotCounts =
          dotCountType === "fixed"
            ? Array.from({ length: cardsToGenerate }, () => itemCount)
            : buildNumberSenseDotCounts(cardsToGenerate, minDots, maxDots);
        for (let i = 0; i < cardsToGenerate; i++) {
          const dotCount = dotCounts[i] ?? itemCount;
          const colors = dotColors.length > 0 ? dotColors : ["blue"];
          const shapes = dotShapes.length > 0 ? dotShapes : ["circle"];
          const image = generateNumberSenseCard(
            dotCount,
            colors,
            shapes,
            shouldMixNumberSenseCard(dotMixMode, dotCount, colors, shapes),
          );
          generatedItems.push({
            name: image.name,
            svg: image.svg,
            count: image.count,
          });
        }
        generatedSequence = generatedItems;
        break;
      }
      case "periodicTable":
        generatedSequence = generatePeriodicTableElements(countToGenerate).map(
          (element) => ({ name: element }),
        );
        break;
      case "scienceTerms":
        generatedSequence = generateScienceTerms(countToGenerate).map(
          (term) => ({
            name: term,
          }),
        );
        break;
      case "nursingTerms":
        generatedSequence = generateNursingTerms(countToGenerate).map(
          (term) => ({
            name: term,
          }),
        );
        break;
      case "fullPeriodicTable":
        generatedSequence = generateFullPeriodicTable().map((element) => ({
          name: element,
        }));
        break;
      case "numbersOneToTwenty":
        generatedSequence = generateNumbersOneToTwenty().map((num) => ({
          name: num.toString(),
        }));
        break;
      case "Domino Comparison": {
        const dominoItems = generateDominoComparisons(
          countToGenerate,
          minDominoDots,
          maxDominoDots,
          dominoDotColor,
        );

        generatedSequence = dominoItems.map((item) => ({
          name: item.name,
          svg: item.svg,
          count: item.answer === "left" ? 1 : item.answer === "right" ? 2 : 0,
        }));
        break;
      }
      default:
        console.error("Invalid type selected");
        alert("Please select a valid item type before generating.");
        return;
    }

    console.log("Generated sequence:", generatedSequence);
    setSequence(generatedSequence);
    setPreviewSequence(generatedSequence);
    setFile([]);
    setIsGenerated(true);
  };
  const handleNext = () => {
    if (!collectionName) {
      alert("Collection name is missing. Please go back and enter a name.");
      return;
    }
    if (category === PICTURE_QUIZ_CATEGORY) {
      if (!validatePictureQuizImages()) {
        return;
      }
      handleSaveCollection();
      return;
    }
    if (category === PICTURE_WORDS_CATEGORY) {
      if (!validatePictureWordsImages()) {
        return;
      }
      handleSaveCollection();
      return;
    }
    // Check if a random sequence was generated or a file was uploaded
    if (isGenerated || file.length > 0) {
      handleSaveCollection(); // Call the save function directly
    } else {
      // Navigate to CollectionFinalStep if no sequence or file
      navigate("/collection-final-step", {
        state: { collectionName, isPublic, category, sequence, type, stage },
      });
    }
  };
  const handleClear = () => {
    setIsGenerated(false);
    setSequence([]);
    setPreviewSequence([]);
    setFile([]);
    setImages([]);
    setPictureQuizSaveAttempted(false);
    setPictureWordsSaveAttempted(false);
  };
  const handleSaveCollection = async () => {
    console.log("Preparing to save collection");
    try {
      if (!currentUser || !currentUser.username) {
        throw new Error("Current user is undefined");
      }
      // Create collection data based on whether we have images or sequence
      let collectionData;
      if (category === PICTURE_QUIZ_CATEGORY) {
        if (!validatePictureQuizImages()) {
          return;
        }
        collectionData = images.map((image) => ({
          name: image.question?.trim() || DEFAULT_PICTURE_QUIZ_PROMPT,
          ...slideImageFields(image.preview, image.secondPreview),
          answer: image.answer?.trim(),
          type: "image",
        }));
      } else if (category === PICTURE_WORDS_CATEGORY) {
        if (!validatePictureWordsImages()) {
          return;
        }
        collectionData = images.map((image) => ({
          name: image.word?.trim() || "",
          ...slideImageFields(image.preview, image.secondPreview),
          type: "image",
        }));
      } else if (file.length > 0) {
        collectionData = images.map((image) => ({
          name: image.file.name,
          ...slideImageFields(image.preview, image.secondPreview),
          count: category === "Number Sense" ? image.count : undefined,
          type: "image",
        }));
      } else {
        collectionData = previewSequence.map((item, index) => ({
          id: index + 1,
          name: item.name,
          svg: item.svg,
          count: item.count,
          type: "sequence",
        }));
      }
      console.log("Saving collection data:", collectionData);
      await saveCollection(
        currentUser.username,
        collectionName,
        collectionData,
        isPublic ? "public" : "private",
        category,
        type,
        getAccessTokenSilently,
        stage,
      );
      navigate("/your-collections");
    } catch (error) {
      console.error("Error saving collection:", error);
      alert("There was an error saving your collection. Please try again.");
    }
  };
  const shouldHideQuantity = (selectedType: string): boolean => {
    return [
      "numbersOneToHundred",
      "alphabet",
      "fullPeriodicTable",
      "numbersOneToTwenty",
    ].includes(selectedType);
  };

  /**
   * Unique-item pools that cannot supply more than this many distinct values.
   * Returning null means there is no hard uniqueness cap for that type.
   */
  const getMaxUniqueItemsForType = (selectedType: string): number | null => {
    switch (selectedType) {
      case "numbers":
        return 100;
      case "letters":
      case "randomLowercase":
        return 26;
      case "randomMixedCase":
      case "alphabet":
        return 52;
      case "numbersOneToTwenty":
        return 20;
      case "numbersOneToHundred":
        return 100;
      case "periodicTable":
      case "fullPeriodicTable":
        return 118;
      default:
        return null;
    }
  };

  /** Soft cap to keep previews responsive for types that allow repeats */
  const SOFT_MAX_GENERATED_ITEMS = 200;

  const getEffectiveItemLimit = (selectedType: string): number => {
    const uniqueMax = getMaxUniqueItemsForType(selectedType);
    if (uniqueMax !== null) {
      return uniqueMax;
    }
    return SOFT_MAX_GENERATED_ITEMS;
  };

  const clampItemCount = (count: number, selectedType = type): number => {
    if (!Number.isFinite(count) || count < 1) {
      return 1;
    }
    return Math.min(count, getEffectiveItemLimit(selectedType));
  };

  const handleItemCountChange = (rawValue: string) => {
    if (rawValue === "") {
      setItemCount(1);
      return;
    }
    const count = parseInt(rawValue, 10);
    if (Number.isNaN(count)) {
      return;
    }
    setItemCount(clampItemCount(count));
  };
  // Function to edit an item in the preview sequence
  const editItem = (index: number, newName: string) => {
    const updatedSequence = [...previewSequence];
    updatedSequence[index].name = newName; // Update the name
    setPreviewSequence(updatedSequence); // Set the updated sequence
  };
  // Function to delete an item from the preview sequence
  const deleteItem = (index: number) => {
    const updatedSequence = previewSequence.filter((_, i) => i !== index);
    setPreviewSequence(updatedSequence); // Set the updated sequence
  };
  // Add this handler function near your other handlers
  const handleRemoveImage = (id: string) => {
    setImages((prevImages) => prevImages.filter((image) => image.id !== id));
  };
  const getCardBackgroundColor = () => {
    return "bg-white"; // Default styles
  };
  const getTextColorForBackground = () => {
    return "text-black"; // Default styles
  };
  const handleCreateCustomItems = () => {
    // Ensure you are accessing the correct property of the generated items
    const customItems = previewSequence.map((item) => item.name); // Access the "name" property
    navigate("/collection-final-step", {
      state: {
        collectionName,
        isPublic,
        category,
        sequence: customItems, // Pass the correct array of names
        type,
        stage,
      },
    });
  };
  const handleAddItem = () => {
    if (newItemName.trim()) {
      setPreviewSequence((prev) => [
        ...prev,
        { name: newItemName, svg: undefined, count: undefined },
      ]);
      setNewItemName(""); // Clear the input after adding
    } else {
      alert("Item name cannot be empty."); // Alert if the input is empty
    }
  };
  if (!currentUser) {
    return <div className="p-4 text-center">Loading user information...</div>;
  }
  return (
    <div
      className="page-container text-theme-text"
    >
      <div
        className="flex w-full flex-col items-center pt-5"
        style={{
          backgroundColor: theme.backgroundColor,
          color: theme.textColor,
        }}
      >
        <h1 className="collection-setup text-2xl font-bold sm:text-4xl">
          <BubbleText>Step 2 - Setup</BubbleText>
        </h1>
        <h2 className="text-3xl font-bold">
          <BubbleText>{`Collection: ${collectionName}`}</BubbleText>
        </h2>
        <h3 className="mb-4 text-2xl font-semibold">
          <BubbleText>{`Category: ${category}`}</BubbleText>
        </h3>
        <div className="mb-4 w-full max-w-4xl px-4">
          <div
            className={`mx-auto mb-8 max-w-md rounded-lg border p-6 shadow-md ${getCardBackgroundColor()} ${getTextColorForBackground()}`}
            style={{
              borderColor: "black",
            }}
          >
            <h4 className="mb-4 text-center text-xl font-bold">
              Collection Settings
            </h4>
            <div className="mx-auto space-y-4">
              {category === PICTURE_QUIZ_CATEGORY ? (
                <p className="text-center text-sm opacity-80">
                  Upload images below. Each picture needs a question and an
                  answer before you can save. Use “Add a second image beside
                  this” when two pictures should share one slide.
                </p>
              ) : category === PICTURE_WORDS_CATEGORY ? (
                <p className="text-center text-sm opacity-80">
                  Upload images below. Enter the word that matches each picture
                  before you can save. Use “Add a second image beside this”
                  when two pictures should share one slide.
                </p>
              ) : category !== "Number Sense" ? (
                <>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                    <label
                      htmlFor="typeSelect"
                      className="font-bold sm:whitespace-nowrap"
                    >
                      Type:
                    </label>
                    <select
                      id="typeSelect"
                      className="font-teacher min-h-[44px] w-full rounded border border-black bg-white p-2 text-center text-base text-black sm:w-auto"
                      value={type}
                      onChange={(e) => {
                        setType(e.target.value);
                        console.log("Type changed to:", e.target.value); // Debugging log for type change
                      }}
                    >
                      {category === "Math" && (
                        <>
                          <option value="numbers">Numbers</option>
                          <option value="numbersOneToHundred">
                            Numbers 1-100
                          </option>
                          <option value="mathProblems">Math Problems</option>
                          <option value="numbersOneToTwenty">
                            Numbers 1-20
                          </option>
                        </>
                      )}
                      {category === "Language Arts" && (
                        <>
                          <option value="letters">Uppercase Letters</option>
                          <option value="randomLowercase">
                            Lowercase Letters
                          </option>
                          <option value="randomMixedCase">
                            Mixed Case Letters
                          </option>
                          <option value="alphabet">Full Alphabet</option>
                        </>
                      )}
                      {category === "Science" && (
                        <>
                          <option value="periodicTable">Periodic Table</option>
                          <option value="fullPeriodicTable">
                            Full Periodic Table
                          </option>
                          <option value="scienceTerms">Science Terms</option>
                        </>
                      )}
                      {category === "Nursing" && (
                        <option value="nursingTerms">Nursing Terms</option>
                      )}
                    </select>
                  </div>
                  {!shouldHideQuantity(type) && (
                    <>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                        <label
                          htmlFor="itemCount"
                          className="font-bold sm:whitespace-nowrap"
                        >
                          Number of Items in Collection:
                        </label>
                        <input
                          type="number"
                          id="itemCount"
                          className={`font-teacher min-h-[44px] w-full rounded-md border border-black bg-white p-2 text-center text-base text-black sm:w-auto`}
                          value={itemCount}
                          min={1}
                          max={getEffectiveItemLimit(type)}
                          onWheel={preventNumberInputScroll}
                          onChange={(e) =>
                            handleItemCountChange(e.target.value)
                          }
                        />
                      </div>
                      {getMaxUniqueItemsForType(type) !== null ? (
                        <p className="text-center text-sm opacity-80">
                          Max {getMaxUniqueItemsForType(type)} unique items for
                          this type.
                        </p>
                      ) : (
                        <p className="text-center text-sm opacity-80">
                          Max {SOFT_MAX_GENERATED_ITEMS} items per generate.
                        </p>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                    <label
                      htmlFor="dotCountType"
                      className="font-bold sm:whitespace-nowrap"
                    >
                      Dot Count Type:
                    </label>
                    <select
                      id="dotCountType"
                      className="font-teacher min-h-[44px] w-full rounded-md border border-black bg-white p-2 text-center text-base text-black sm:w-auto"
                      value={dotCountType}
                      onChange={(e) => {
                        setDotCountType(e.target.value as "fixed" | "random");
                        console.log(
                          "Dot count type changed to:",
                          e.target.value,
                        ); // Debugging log for dot count type change
                      }}
                    >
                      <option value="random">Random</option>
                      <option value="fixed">Fixed</option>
                    </select>
                  </div>
                  {dotCountType === "fixed" ? (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                      <label
                        htmlFor="itemCount"
                        className="font-bold sm:whitespace-nowrap"
                      >
                        Number of Dots:
                      </label>
                      <input
                        type="number"
                        id="itemCount"
                        className={`font-teacher min-h-[44px] w-full rounded-md border border-black bg-white p-2 text-center text-base text-black sm:w-auto`}
                        value={itemCount}
                        min={1}
                        onWheel={preventNumberInputScroll}
                        onChange={(e) => {
                          const count = parseInt(e.target.value, 10);
                          setItemCount(count);
                          console.log("Item count changed to:", count); // Debugging log for item count change
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                        <label
                          htmlFor="minDots"
                          className="font-bold sm:whitespace-nowrap"
                        >
                          Minimum Dots:
                        </label>
                        <input
                          type="number"
                          id="minDots"
                          className={`font-teacher min-h-[44px] w-full rounded-md border border-black bg-white p-2 text-center text-base text-black sm:w-auto`}
                          value={minDots}
                          min={1}
                          onWheel={preventNumberInputScroll}
                          onChange={(e) => {
                            const count = parseInt(e.target.value, 10);
                            setMinDots(count);
                            console.log("Minimum dots changed to:", count); // Debugging log for min dots change
                          }}
                        />
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                        <label
                          htmlFor="maxDots"
                          className="font-bold sm:whitespace-nowrap"
                        >
                          Maximum Dots:
                        </label>
                        <input
                          type="number"
                          id="maxDots"
                          className={`font-teacher min-h-[44px] w-full rounded-md border border-black bg-white p-2 text-center text-base text-black sm:w-auto`}
                          value={maxDots}
                          min={minDots}
                          onWheel={preventNumberInputScroll}
                          onChange={(e) => {
                            const count = parseInt(e.target.value, 10);
                            setMaxDots(count);
                            console.log("Maximum dots changed to:", count); // Debugging log for max dots change
                          }}
                        />
                      </div>
                    </>
                  )}
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                    <label
                      htmlFor="collectionItemCount"
                      className="font-bold sm:whitespace-nowrap"
                    >
                      Number of Cards:
                    </label>
                    <input
                      type="number"
                      id="collectionItemCount"
                      className="font-teacher min-h-[44px] w-full rounded-md border border-black bg-white p-2 text-center text-base text-black sm:w-auto"
                      value={collectionItemCount}
                      min={1}
                      max={SOFT_MAX_GENERATED_ITEMS}
                      onWheel={preventNumberInputScroll}
                      onChange={(e) => {
                        const count = parseInt(e.target.value, 10);
                        if (Number.isNaN(count)) {
                          return;
                        }
                        setCollectionItemCount(
                          Math.min(
                            Math.max(1, count),
                            SOFT_MAX_GENERATED_ITEMS,
                          ),
                        );
                      }}
                    />
                  </div>
                  {dotCountType === "random" ? (
                    <p className="text-sm opacity-80">
                      Random gives each card a different number of dots between
                      min and max. Ten cards from 1–10 is a mix, not ten 10s.
                    </p>
                  ) : null}
                  <div id="dot-color" className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-bold">Dot Colors</p>
                      <button
                        type="button"
                        className="text-sm underline"
                        onClick={() =>
                          setDotColors([...NUMBER_SENSE_COLORS])
                        }
                      >
                        Select all
                      </button>
                    </div>
                    <p className="text-sm opacity-80">
                      Pick one color for every card, or several to mix.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {NUMBER_SENSE_COLORS.map((color) => (
                        <label
                          key={color}
                          className="flex items-center gap-1 capitalize"
                        >
                          <input
                            type="checkbox"
                            checked={dotColors.includes(color)}
                            onChange={() =>
                              toggleUniqueValue(color, setDotColors)
                            }
                          />
                          {color}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div id="dot-shape" className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-bold">Dot Shapes</p>
                      <button
                        type="button"
                        className="text-sm underline"
                        onClick={() =>
                          setDotShapes([...NUMBER_SENSE_SHAPES])
                        }
                      >
                        Select all
                      </button>
                    </div>
                    <p className="text-sm opacity-80">
                      Pick one shape for every card, or several to mix.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {NUMBER_SENSE_SHAPES.map((shape) => (
                        <label
                          key={shape}
                          className="flex items-center gap-1 capitalize"
                        >
                          <input
                            type="checkbox"
                            checked={dotShapes.includes(shape)}
                            onChange={() =>
                              toggleUniqueValue(shape, setDotShapes)
                            }
                          />
                          {shape}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div id="dot-mix-mode" className="space-y-2">
                    <label htmlFor="dotMixMode" className="font-bold">
                      Mix on one card
                    </label>
                    <p className="text-sm opacity-80">
                      Mixed cards can ask how many are a color or shape, such as
                      “How many triangles?” Needs at least 2 dots and more than
                      one color or shape.
                    </p>
                    <select
                      id="dotMixMode"
                      className="font-teacher w-full rounded-md border border-black bg-white p-2 text-black"
                      value={dotMixMode}
                      onChange={(event) =>
                        setDotMixMode(
                          event.target.value as NumberSenseMixMode,
                        )
                      }
                    >
                      <option value="never">
                        Never — each card is one color and one shape
                      </option>
                      <option value="sometimes">
                        Sometimes — some cards mix colors or shapes
                      </option>
                      <option value="always">
                        Always — mix colors or shapes on every card
                      </option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
          {category === PICTURE_QUIZ_CATEGORY || category === PICTURE_WORDS_CATEGORY ? (
            <div
              className={`rounded-lg border p-6 shadow-md ${getCardBackgroundColor()} ${getTextColorForBackground()}`}
              style={{
                borderColor: "black",
              }}
            >
              <h4 className="mb-4 text-xl font-bold">Upload Pictures</h4>
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                {category === PICTURE_QUIZ_CATEGORY
                  ? "Add images for your quiz. Enter a question and answer for each picture. Add a second image on a card when both pictures belong on one slide."
                  : "Add images and enter the word for each picture. Students will learn to match the image with the word. Add a second image on a card when both pictures belong on one slide."}
              </p>
              <input
                title="Upload Images"
                placeholder="Upload Images"
                type="file"
                id={
                  category === PICTURE_QUIZ_CATEGORY
                    ? "pictureQuizUpload"
                    : "pictureWordsUpload"
                }
                className={
                  category === PICTURE_QUIZ_CATEGORY
                    ? "picture-quiz-upload w-full rounded-md border border-gray-300 bg-white p-2"
                    : "picture-words-upload w-full rounded-md border border-gray-300 bg-white p-2"
                }
                onChange={handleFileChange}
                accept="image/*"
                multiple
              />
            </div>
          ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div
              className={`rounded-lg border p-6 shadow-md ${getCardBackgroundColor()} ${getTextColorForBackground()}`}
              style={{
                borderColor: "black",
              }}
            >
              <h4 className="mb-4 text-xl font-bold">Generate Random Items</h4>
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                Quickly create a collection with randomly generated items based
                on your settings.
              </p>
              <div className="mt-auto pt-[42px]">
                <button
                  type="button"
                  className="generate-random-sequence-button w-full rounded-md border border-black bg-green-500 px-4 py-2 font-bold uppercase text-white transition duration-300 hover:bg-green-600"
                  onClick={generateRandomSequence}
                >
                  Generate Random Sequence
                </button>
              </div>
            </div>
            <div
              className={`rounded-lg border p-6 shadow-md ${getCardBackgroundColor()} ${getTextColorForBackground()}`}
              style={{
                borderColor: "black",
              }}
            >
              <h4 className="mb-4 text-xl font-bold">Create Custom Items</h4>
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                Create your own custom items or upload files for your
                collection. Each file is one slide. On an image, you can add a
                second picture so both show side by side.
              </p>
              <div className="space-y-4">
                <input
                  title="Upload Files"
                  placeholder="Upload Files"
                  type="file"
                  id="fileUpload"
                  className="w-full rounded-md border border-gray-300 bg-white p-2"
                  onChange={handleFileChange}
                  accept="image/*,.pdf"
                  multiple
                />
                <button
                  type="button"
                  className="create-custom-items-button w-full rounded-lg border border-black bg-blue-500 px-6 py-2 font-bold text-white transition-all duration-300 hover:bg-blue-600"
                  onClick={handleCreateCustomItems}
                >
                  Create Custom Items
                </button>
              </div>
            </div>
          </div>
          )}
          {(isGenerated || images.length > 0) && (
            <div
              className={`mt-8 rounded-lg border p-6 shadow-md ${getCardBackgroundColor()} ${getTextColorForBackground()}`}
              style={{
                borderColor: "black",
              }}
            >
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h4 className="text-xl font-bold">Preview</h4>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="clear-button min-h-[44px] rounded-md bg-yellow-500 px-4 py-2 font-bold text-white hover:bg-yellow-600"
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="next-button min-h-[44px] rounded-md bg-green-500 px-4 py-2 font-bold text-white hover:bg-green-600"
                    onClick={handleNext}
                  >
                    Save Collection
                  </button>
                </div>
              </div>
              {images.length === 0 && (
                <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <input
                    type="text"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    placeholder="Add new item"
                    className="min-h-[44px] w-full rounded border bg-white p-2 text-base sm:flex-1"
                  />
                  <button
                    type="button"
                    className="min-h-[44px] rounded-md bg-blue-500 px-4 py-2 font-bold text-white hover:bg-blue-600"
                    onClick={handleAddItem}
                  >
                    Add Item
                  </button>
                </div>
              )}
              {isGenerated && (
                <div className="mt-6">
                  <h3 className="generated-sequence-preview mb-2 text-center text-xl font-bold">
                    Generated Sequence Preview:
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                    {previewSequence.map((item, index) => (
                      <div
                        key={index}
                        className="rounded-md border border-black p-2"
                      >
                        <p className="text-center">{item.name}</p>
                        {category === "Math" && type === "mathProblems" && (
                          <div className="mt-2">
                            <label className="block text-sm font-bold text-gray-600">
                              Answer:
                            </label>
                            <input
                              title="Answer"
                              placeholder="Answer"
                              type="number"
                              value={item.count || ""}
                              readOnly
                              className="w-full rounded border border-gray-300 bg-gray-100 px-2 py-1 text-center"
                            />
                          </div>
                        )}
                        {item.svg && (
                          <>
                            <ColorblindImage
                              src={item.svg}
                              alt={item.name}
                              className="h-auto w-full"
                            />
                            {category === "Number Sense" ? (
                              <p className="mt-1 text-center text-sm">
                                Answer: {item.count}
                              </p>
                            ) : null}
                          </>
                        )}
                        <div className="mt-2 flex justify-between">
                          <button
                            type="button"
                            onClick={() =>
                              editItem(
                                index,
                                prompt("Edit item name:", item.name) ||
                                  item.name,
                              )
                            }
                            className="text-blue-500"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteItem(index)}
                            className="text-red-500"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {images.length > 0 && (
                <div className="mt-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {images.map((image) => (
                      <div
                        key={image.id}
                        className="relative rounded-md border border-black p-3"
                      >
                        {image.file.type.startsWith("image/") ? (
                          <div className="mb-3">
                            <PairSlideImages
                              primarySrc={image.preview}
                              secondarySrc={image.secondPreview}
                              alt="Preview"
                              imageClassName="mx-auto max-h-40 w-full object-contain"
                              onSecondaryChange={(src) =>
                                handleSecondImageChange(image.id, src)
                              }
                            />
                          </div>
                        ) : (
                          <div className="mb-3 flex h-40 items-center justify-center rounded border border-gray-300 bg-gray-50">
                            <span className="text-sm">PDF Document</span>
                          </div>
                        )}
                        {category === PICTURE_QUIZ_CATEGORY && (
                          <div className="space-y-2">
                            <div>
                              <label className="block text-sm font-bold">
                                Question
                              </label>
                              <input
                                type="text"
                                value={image.question || ""}
                                onChange={(e) =>
                                  handlePictureQuizQuestionChange(
                                    image.id,
                                    e.target.value,
                                  )
                                }
                                placeholder={DEFAULT_PICTURE_QUIZ_PROMPT}
                                className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-bold">
                                Answer <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={image.answer || ""}
                                onChange={(e) =>
                                  handlePictureQuizAnswerChange(
                                    image.id,
                                    e.target.value,
                                  )
                                }
                                placeholder="Enter the answer"
                                className={`mt-1 w-full rounded border px-2 py-1 ${
                                  (pictureQuizSaveAttempted ||
                                    image.answerError) &&
                                  !image.answer?.trim()
                                    ? "border-red-500 bg-red-50"
                                    : "border-gray-300"
                                }`}
                              />
                            </div>
                          </div>
                        )}
                        {category === PICTURE_WORDS_CATEGORY && (
                          <div>
                            <label className="block text-sm font-bold">
                              Word <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={image.word || ""}
                              onChange={(e) =>
                                handlePictureWordsWordChange(
                                  image.id,
                                  e.target.value,
                                )
                              }
                              placeholder="e.g. apple"
                              className={`mt-1 w-full rounded border px-2 py-1 ${
                                (pictureWordsSaveAttempted ||
                                  image.wordError) &&
                                !image.word?.trim()
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                              }`}
                            />
                          </div>
                        )}
                        {category === "Number Sense" && (
                          <input
                            title="Count"
                            placeholder="Count"
                            type="number"
                            value={image.count ?? ""}
                            onWheel={preventNumberInputScroll}
                            onChange={(e) =>
                              handleImageCountChange(
                                image.id,
                                parseInt(e.target.value),
                              )
                            }
                            min="1"
                            className="mt-2 w-full rounded border border-gray-300 px-2 py-1"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(image.id)}
                          className="absolute -right-2 -top-2 rounded-full bg-red-500 px-2 py-1 text-xs text-white"
                        >
                          X
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {category === "Domino Comparison" && (
            <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="minDominoDots" className="mb-2 block">
                  Minimum Dots Per Side
                </label>
                <input
                  id="minDominoDots"
                  type="number"
                  min="1"
                  max="9"
                  value={minDominoDots}
                  onWheel={preventNumberInputScroll}
                  onChange={(e) => setMinDominoDots(parseInt(e.target.value))}
                  className="w-full rounded border p-2"
                />
              </div>
              <div>
                <label htmlFor="maxDominoDots" className="mb-2 block">
                  Maximum Dots Per Side
                </label>
                <input
                  id="maxDominoDots"
                  type="number"
                  min="1"
                  max="9"
                  value={maxDominoDots}
                  onWheel={preventNumberInputScroll}
                  onChange={(e) => setMaxDominoDots(parseInt(e.target.value))}
                  className="w-full rounded border p-2"
                />
              </div>
              <div>
                <label htmlFor="dominoDotColor" className="mb-2 block">
                  Dot Color
                </label>
                <select
                  id="dominoDotColor"
                  value={dominoDotColor}
                  onChange={(e) => setDominoDotColor(e.target.value)}
                  className="w-full rounded border p-2"
                >
                  <option value="black">Black</option>
                  <option value="red">Red</option>
                  <option value="blue">Blue</option>
                  <option value="green">Green</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
      <GuidedTour
        steps={steps}
        isRunning={isTourRunning}
        onComplete={handleTourComplete}
        currentStep={currentTourStep}
        onStepChange={handleTourStepChange}
        tourName="collectionSetup"
      />
    </div>
  );
};
export default CollectionSetup;
// Define the type guard function
function isUser(user: unknown): user is User {
  return (
    typeof user === "object" &&
    user !== null &&
    typeof (user as User).username === "string"
  );
}
// Define the function to generate a unique ID
function generateId(): string {
  return Math.random().toString(36).slice(2, 11);
}
